import {
  createContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  type ReactNode,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useSidebar } from "@/components/ui/sidebar";
import { TOUR_STEPS } from "./tour-steps";
import { useTourStatus } from "@/hooks/use-tour-status";
import type { ProductTourContextValue, TargetRect } from "./types";

export const ProductTourContext = createContext<ProductTourContextValue | null>(
  null
);

const SPOTLIGHT_PADDING = 8;
const TARGET_RETRY_DELAY = 300;
const TARGET_MAX_RETRIES = 10;

export function ProductTourProvider({ children }: { children: ReactNode }) {
  const [isActive, setIsActive] = useState(false);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [targetRect, setTargetRect] = useState<TargetRect | null>(null);

  const { isCompleted: tourDone, markCompleted } = useTourStatus();
  const navigate = useNavigate();
  const location = useLocation();
  const { setOpen, isMobile, setOpenMobile, open } = useSidebar();

  const sidebarWasOpen = useRef(true);
  const pendingStepRef = useRef<number | null>(null);

  const currentStep =
    isActive && currentStepIndex < TOUR_STEPS.length
      ? TOUR_STEPS[currentStepIndex]
      : null;

  // ─── Measure target element ─────────────────────────────────────────────────
  const measureTarget = useCallback(
    (selector: string | null, retries = 0): void => {
      if (!selector) {
        setTargetRect(null);
        return;
      }

      const el = document.querySelector(selector);
      if (el) {
        const rect = el.getBoundingClientRect();
        setTargetRect({
          x: rect.x - SPOTLIGHT_PADDING,
          y: rect.y - SPOTLIGHT_PADDING,
          width: rect.width + SPOTLIGHT_PADDING * 2,
          height: rect.height + SPOTLIGHT_PADDING * 2,
        });

        // Scroll into view if needed
        const inViewport =
          rect.top >= 0 &&
          rect.left >= 0 &&
          rect.bottom <= window.innerHeight &&
          rect.right <= window.innerWidth;

        if (!inViewport) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          // Re-measure after scroll
          requestAnimationFrame(() => {
            const r2 = el.getBoundingClientRect();
            setTargetRect({
              x: r2.x - SPOTLIGHT_PADDING,
              y: r2.y - SPOTLIGHT_PADDING,
              width: r2.width + SPOTLIGHT_PADDING * 2,
              height: r2.height + SPOTLIGHT_PADDING * 2,
            });
          });
        }
      } else if (retries < TARGET_MAX_RETRIES) {
        setTimeout(
          () => measureTarget(selector, retries + 1),
          TARGET_RETRY_DELAY
        );
      } else {
        // Target not found after retries — skip spotlight
        setTargetRect(null);
      }
    },
    []
  );

  // ─── Ensure sidebar is expanded for sidebar steps ───────────────────────────
  const ensureSidebarOpen = useCallback(() => {
    if (isMobile) {
      setOpenMobile(true);
    } else {
      setOpen(true);
    }
  }, [isMobile, setOpen, setOpenMobile]);

  // ─── Apply step (navigate + measure) ────────────────────────────────────────
  const applyStep = useCallback(
    (index: number) => {
      const step = TOUR_STEPS[index];
      if (!step) return;

      // If step targets sidebar item, ensure sidebar is open
      if (step.target?.includes("nav-")) {
        ensureSidebarOpen();
      }

      // If step requires navigation to a different route
      const targetPath = step.navigateTo;
      if (targetPath && location.pathname !== targetPath) {
        pendingStepRef.current = index;
        navigate(targetPath);
        return; // measurement will happen in the location effect
      }

      // No navigation needed — measure directly with delay for rendering
      setTimeout(() => measureTarget(step.target), 100);
    },
    [ensureSidebarOpen, location.pathname, navigate, measureTarget]
  );

  // ─── React to route changes (for multi-page steps) ─────────────────────────
  useEffect(() => {
    if (pendingStepRef.current !== null && isActive) {
      const idx = pendingStepRef.current;
      const step = TOUR_STEPS[idx];
      pendingStepRef.current = null;

      if (step) {
        // Delay to let the lazy-loaded page render
        setTimeout(() => {
          if (step.target?.includes("nav-")) {
            ensureSidebarOpen();
          }
          measureTarget(step.target);
        }, 600);
      }
    }
  }, [location.pathname, isActive, measureTarget, ensureSidebarOpen]);

  // ─── Recalculate on resize ──────────────────────────────────────────────────
  useEffect(() => {
    if (!isActive || !currentStep?.target) return;

    const handleResize = () => measureTarget(currentStep.target);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isActive, currentStep, measureTarget]);

  // ─── Tour controls ──────────────────────────────────────────────────────────
  // Ref stable pour `open` — evite que startTour soit recree a chaque toggle sidebar
  const openRef = useRef(open);
  openRef.current = open;

  const startTour = useCallback(() => {
    if (tourDone || isActive) return;
    sidebarWasOpen.current = openRef.current;
    setCurrentStepIndex(0);
    setIsActive(true);
    setTargetRect(null);
    // Apply first step
    setTimeout(() => {
      const step = TOUR_STEPS[0];
      if (step?.target?.includes("nav-")) {
        if (isMobile) setOpenMobile(true);
        else setOpen(true);
      }
      measureTarget(TOUR_STEPS[0]?.target ?? null);
    }, 100);
  }, [tourDone, isActive, isMobile, setOpen, setOpenMobile, measureTarget]);

  const endTour = useCallback(() => {
    setIsActive(false);
    setCurrentStepIndex(0);
    setTargetRect(null);
    markCompleted();
    // Restore sidebar state
    if (!sidebarWasOpen.current) {
      if (isMobile) setOpenMobile(false);
      else setOpen(false);
    }
  }, [markCompleted, isMobile, setOpen, setOpenMobile]);

  const nextStep = useCallback(() => {
    const nextIndex = currentStepIndex + 1;
    if (nextIndex >= TOUR_STEPS.length) {
      endTour();
      return;
    }
    setCurrentStepIndex(nextIndex);
    applyStep(nextIndex);
  }, [currentStepIndex, applyStep, endTour]);

  const prevStep = useCallback(() => {
    const prevIndex = currentStepIndex - 1;
    if (prevIndex < 0) return;
    setCurrentStepIndex(prevIndex);
    applyStep(prevIndex);
  }, [currentStepIndex, applyStep]);

  const skipTour = useCallback(() => {
    endTour();
    // Navigate back to dashboard if not already there
    if (location.pathname !== "/") {
      navigate("/");
    }
  }, [endTour, location.pathname, navigate]);

  const value: ProductTourContextValue = {
    isActive,
    currentStepIndex,
    currentStep,
    targetRect,
    totalSteps: TOUR_STEPS.length,
    progress: TOUR_STEPS.length > 0 ? currentStepIndex / (TOUR_STEPS.length - 1) : 0,
    startTour,
    nextStep,
    prevStep,
    skipTour,
  };

  return (
    <ProductTourContext.Provider value={value}>
      {children}
    </ProductTourContext.Provider>
  );
}
